import React from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { getUpgradeCheckoutPath, UPGRADE_CHECKOUT_BASE } from "../../../helpers/getUpgradePath";
import CloseIcon from "@mui/icons-material/Close";
import GuideVideoModal from "../../GuideVideoModal";
import { guideVideoData } from "../../GuideVideoModal/data";

const getDefaultDisclaimer = (history) => (
  <>
    *This feature is available in our{" "}
    <span
      className="font-semibold underline text-[#93C5FD] cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => {
        window.scrollTo(0, 0);
        window.location.href = `${UPGRADE_CHECKOUT_BASE}/starter`;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          window.scrollTo(0, 0);
          window.location.href = `${UPGRADE_CHECKOUT_BASE}/starter`;
        }
      }}
    >
      Starter Pack
    </span>{" "}
    at a special introductory rate of{" "}
    <span className="font-semibold text-[#93C5FD]">$99</span>.
  </>
);

const UpgradeBannerBottom = ({
  className,
  title,
  description,
  disclaimer,
  productAlias,
  upgradeUrl,
  onClose,
  drawerOffset = 0,
  pageKey,
}) => {
  const history = useHistory();
  const [guideOpen, setGuideOpen] = React.useState(false);
  const guideData = pageKey ? guideVideoData.find((d) => d.key === pageKey) : null;
  const targetPath = upgradeUrl || getUpgradeCheckoutPath(productAlias, false);

  const displayTitle = title ?? "Get full access to DAILY BREAD";
  const displayDisclaimer =
    disclaimer !== false ? disclaimer ?? getDefaultDisclaimer(history) : null;

  const scrollToTopAndNavigate = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (targetPath.startsWith("http")) window.location.href = targetPath;
    else history.push(targetPath);
  };

  const handleUpgrade = () => {
    scrollToTopAndNavigate();
  };

  return (
    <div
      className={`fixed bottom-0 z-[1100] flex flex-col items-center justify-center gap-3 border-t border-white/50 bg-[#0061F9] px-4 py-8 text-white shadow-[0_-4px_12px_rgba(0,0,0,0.15)] ${
        className || ""
      }`}
      style={{
        left: drawerOffset,
        width: drawerOffset ? `calc(100% - ${drawerOffset}px)` : "100%",
      }}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/20 hover:text-white"
          aria-label="Close banner"
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </button>
      )}
      <h2 className="text-center text-[18px] font-bold leading-tight md:text-[22px]">
        {displayTitle}
      </h2>

      {description && (
        <p className="mx-auto max-w-[640px] text-center text-[14px] leading-relaxed opacity-95 md:text-[15px]">
          {description}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={guideData ? () => setGuideOpen(true) : handleUpgrade}
          className="flex items-center gap-2 rounded-[4px] border-0 bg-[#EF4444] px-4 py-2 text-[14px] font-semibold text-white transition hover:bg-[#DC2626]"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white">
            <svg
              width="10"
              height="12"
              viewBox="0 0 10 12"
              fill="none"
              className="ml-1"
            >
              <path d="M0 0v12l10-6-10-6z" fill="#EF4444" />
            </svg>
          </span>
          See how it works
        </button>
        <button
          type="button"
          onClick={handleUpgrade}
          className="rounded-[4px] border-[2px] border-[#0061F9] bg-white px-4 py-2 text-[14px] font-bold text-[#1F1F1F] transition hover:bg-[#F0F0F0]"
        >
          Get Full Access
        </button>
      </div>
      {displayDisclaimer && (
        <p className="text-center text-[12px] leading-relaxed opacity-95 italic">
          {displayDisclaimer}
        </p>
      )}
      {guideOpen && guideData && (
        <GuideVideoModal
          {...guideData}
          onClose={() => setGuideOpen(false)}
        />
      )}
    </div>
  );
};

UpgradeBannerBottom.propTypes = {
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disclaimer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.bool,
  ]),
  productAlias: PropTypes.string,
  upgradeUrl: PropTypes.string,
  onClose: PropTypes.func,
  drawerOffset: PropTypes.number,
  pageKey: PropTypes.string,
};

export default UpgradeBannerBottom;
